import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, Switch, Typography } from '@mui/material'

import MainLayout from "../../layouts/MainLayout";
import KeyValueTable from "../Common/KeyValueTable";
import EditDetail from './EditDetail';
import ConfirmationDialog from "../Dialog/ConfirmationDialog";
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import { MixpanelService } from "../../utils/mixpanelService";
import {
  getVariableSet,
  updateVariableSet
} from "../../redux-store/variableSetReducers/variableSetActions";
import {
  deleteVariable,
  createVariable,
  updateVariable,
  replaceValuesWithVarName
} from "../../redux-store/variableReducers/variableActions";
import RawJson from "./RawJson";
import { getProject } from '../../redux-store/currentUserActions';
import { setSelectedProject } from 'reducers/commonSlice.js';


const VariableSet = function ({ project, variableSetId }) {
  const dispatch = useDispatch();
  const { openSnackbar } = useSnackbar();
  const isLoading = useSelector((state) => state.variableSet.isLoading);
  const [title, setTitle] = useState("");
  const [variables, setVariables] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedItemContent, setSelectedItemContent] = useState(null);
  const [variableIdForReplaceValues, setVariableIdForReplaceValues] = useState(null);
  const variableSetDetail = useSelector(state => state.variableSet.selectedVariableSetDetail);

  const [displayAsJson, setDisplayAsJson] = useState(false);
  const [variablesJSON, setVariablesJSON] = useState({});

  const styles = {
    mainLayout: {
      width: "calc(100% - 145px)",
      backgroundColor: 'var(--background-primary)',
      color: 'var(--text-primary)',
    },
    sideDrawer: {
      zIndex: 99,
      backgroundColor: 'var(--background-primary)',
    },
    container: {
      maxHeight: "calc(100vh - 195px)",
      overflow: "auto",
      px: "24px",
      color: "var(--text-primary)",
      backgroundColor: 'var(--background-primary)',
    },
    header: {
      display: 'flex',
      backgroundColor: 'var(--background-primary)',
    },
    headerContent: {
      flexGrow: 1,
    },
    title: {
      color: 'var(--text-primary)',
      fontWeight: 500,
      marginBottom: '8px',
    },
    subtitle: {
      color: 'var(--text-secondary)',
      fontSize: '0.875rem',
    },
    switchContainer: {
      display: 'flex',
      alignItems: 'center',
      color: 'var(--text-primary)',
    },
    switchLabel: {
      color: 'var(--text-primary)',
      marginRight: '8px',
    },
    saveButton: {
      textTransform: 'none',
      fontWeight: 600,
      padding: '0',
      paddingY: '2px',
      fontSize: '14px',
      boxShadow: 'none',
      backgroundColor: 'var(--primary-color)',
      color: 'var(--text-primary)',
      '&:hover': {
        backgroundColor: 'var(--primary-color-dark)',
        boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)',
      },
    },
    errorMessage: {
      color: 'var(--error-color)',
      marginTop: '16px',
    },
  };

  const drawerItems = [
    { icon: 'article', title: 'Details', id: 'detail' }
  ];

  useEffect(() => {
    getVariableSetDetail();
  }, []);

  useEffect(() => {
    if (variableSetDetail) {
      const { variables: fetchedVariables, title: fetchedTitle } = variableSetDetail;
      const formattedVariables = fetchedVariables?.map((v) => formatVariableResponse(v));
      setTitle(fetchedTitle || "");
      setVariables(formattedVariables);
    }
  }, [variableSetDetail])

  const onDeleteVariable = async (variableId) => {
    await dispatch(deleteVariable({ variableId: variableId }));

    setVariables((prev) =>
      prev.filter((variable) => variable.id !== variableId)
    );
  };

  const addNewVariable = useCallback((variable) => {
    if (variable) {
      const formattedVariable = formatVariableResponse(variable);
      setVariables((prev) => [...prev, formattedVariable]);
    }
  }, []);

  const onCreateVariable = useCallback(
    async (variable) => {
      const data = {
        resourceId: variableSetId,
        resourceType: "VariableSet",
        var_name: variable.var_name,
        var_value: variable.var_value
      };
      const newVariableResponse = await dispatch(createVariable(data));
      const { errors = [], variable: createdVariable } = newVariableResponse.payload || {};

      if (errors.length > 0) {
        openSnackbar({ message: errors.join(", "), severity: 'error' });
        return;
      }

      if (variable) {
        openSnackbar({ message: "Variable created successfully", severity: 'success' });
        addNewVariable(createdVariable);
      }
    },
    [dispatch, variableSetId, addNewVariable]
  );

  const updateVariablesUsingJSON = async () => {
    const data = {
      variables: variablesJSON
    };
    await dispatch(updateVariableSet({ variableSetId, data }));
    MixpanelService.track("environment_updated", { variable_set_id: variableSetId });
    openSnackbar({
      message: "Variables updated successfully",
      severity: "success"
    });
  }

  const handleReplaceValuesWithVarName = async (variableId, confirmed) => {
    if (confirmed) {
      const response = await dispatch(replaceValuesWithVarName({ variableId }));
      setVariableIdForReplaceValues(null);
      openSnackbar({
        message: response?.payload?.message,
        severity: "success"
      });
    } else {
      setVariableIdForReplaceValues(variableId);
    }
  }

  const renderReplaceValuesModal = () => {
    return (
      <ConfirmationDialog
        open={!!variableIdForReplaceValues}
        onClose={() => { setVariableIdForReplaceValues(null) }}
        onConfirm={() => { handleReplaceValuesWithVarName(variableIdForReplaceValues, true) }}
        title="Replace Value with Variable Name"
        content="Are you sure you want to replace the existing data in collection and test scenario request with this variable?"
        actionText={"Yes"}
        cancelText={"No"}
      />
    );
  };

  const onSaveNewVariable = useCallback(
    (variable) => {
      if (variable.var_name && variable.var_value) {
        onCreateVariable(variable);
      };
    },
    [onCreateVariable]
  );

  const updateVariableValue = useCallback(
    async ({ id, var_value }) => {
      const variableResponse = await dispatch(
        updateVariable({
          variableValue: var_value,
          variableId: id
        })
      );
      const { variable: updatedVariable } = variableResponse.payload;
      setVariables((prev) =>
        prev.map((variable) =>
          variable.id === id ? updatedVariable : variable
        )
      );
    },
    [dispatch, setVariables]
  );

  const updateSelectedProjectInState = async (projectId) => {
    if(!projectId) return;
    const response = await dispatch(getProject({ projectId: projectId }));
    await dispatch(setSelectedProject(response.payload));
    localStorage.setItem('lastSelectedProject', JSON.stringify(response.payload));
  }

  const getVariableSetDetail = async () => {
    const response = await dispatch(getVariableSet({ variableSetId }));
    await updateSelectedProjectInState(response?.payload?.project_id);
  };

  const handleFormatChange = () => {
    if (!displayAsJson) {
      const json = variables
        .filter(({ is_dynamic }) => is_dynamic === false)
        .reduce((acc, { var_name, var_value }) => {
          acc[var_name] = var_value;
          return acc;
        }, {});
      setVariablesJSON(json);
    }
    setDisplayAsJson(!displayAsJson);
  };

  const formatVariableResponse = ({ id, var_name, var_value, is_dynamic }) => ({
    id,
    var_name,
    var_value,
    isNew: false,
    isEdit: false,
    is_dynamic
  });

  const renderDrawerItem = ({ selectedItem, onDrawerClose }) => {
    const props = { projectId: project.id, variableSetId: variableSetId };
    switch (selectedItem) {
      case 'detail':
        setSelectedItemContent(<EditDetail {...props} closeDrawer={onDrawerClose} />);
        return false;
      default:
        setSelectedItemContent(null);
        return false;
    }
  };

  const renderKeyValueFormat = () => {
    if (displayAsJson) return null;
    return (
      <>
        <KeyValueTable
          allItems={variables}
          onUpdateItem={updateVariableValue}
          onCreateItem={onSaveNewVariable}
          onDeleteItem={onDeleteVariable}
          itemName="variable"
          sxTableContainer={{ borderRadius: "0px" }}
          sxCell={{ fontSize: "13px" }}
          handleReplaceValuesWithVarName={handleReplaceValuesWithVarName}
        />
        {renderReplaceValuesModal()}
      </>
    )
  }

  const renderJSONFormat = () => {
    if (!displayAsJson) return null;
    return (
      <>
        <RawJson body={variablesJSON} onBodyChange={setVariablesJSON} />
        <Button
          variant="contained"
          color="primary"
          onClick={updateVariablesUsingJSON}
          sx={styles.saveButton}
        >
          Save
        </Button>
      </>
    )
  }

  return (
    <MainLayout
      isLoading={isLoading}
      headerText={title}
      subtitleText="Environment"
      drawerItems={drawerItems}
      renderDrawerItem={renderDrawerItem}
      enableSideDrawer={true}
      sx={styles.mainLayout}
      selectedItemContent={selectedItemContent}
      sxSideDrawer={styles.sideDrawer}
    >
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.headerContent}>
            <Typography variant="h5" sx={styles.title}>
              All environments
            </Typography>
            <Typography variant="body2" sx={styles.subtitle}>
              Key must be unique • Press enter to add/update set
            </Typography>
          </Box>
          <Box sx={styles.switchContainer}>
            <Typography sx={styles.switchLabel}>
              Show as JSON
            </Typography>
            <Switch
              checked={displayAsJson}
              onChange={handleFormatChange}
              color="primary"
            />
          </Box>
        </Box>

        {renderKeyValueFormat()}
        {renderJSONFormat()}

        {errorMsg && (
          <Typography sx={styles.errorMessage}>
            {errorMsg}
          </Typography>
        )}
      </Box>
    </MainLayout>
  );
};

export default VariableSet;
